import { Avatar, Button } from "@heroui/react";

import { LogOutIcon } from "@/src/components/icons";
import { useSession } from "@/src/contexts/AuthContext";
import { useTranslations } from "@/src/i18n/i18n";
import { initials } from "@/src/libs/initials";

/**
 * Le compte connecté, en pied de barre latérale.
 *
 * Déconnexion : on **vide la session, sans naviguer**. `RequireAuth` s'occupe
 * de la redirection — un `navigate()` en plus ferait une seconde transition
 * pour rien (cf. § Routage du README).
 */
export function AccountCard({
  isCollapsed = false,
}: {
  isCollapsed?: boolean;
}) {
  const { session, signOut } = useSession();
  const t = useTranslations("global");

  if (!session) return null;

  const role = session.roles[0];

  return (
    <div className="flex flex-col gap-1">
      <div
        className={`flex items-center gap-3 rounded-lg p-2 ${isCollapsed ? "justify-center" : ""}`}
      >
        <Avatar size="sm">
          {session.avatarUrl ? (
            <Avatar.Image alt="" src={session.avatarUrl} />
          ) : null}
          <Avatar.Fallback>{initials(session.username)}</Avatar.Fallback>
        </Avatar>

        {isCollapsed ? null : (
          // `min-w-0` : sans lui, un e-mail long élargit la barre latérale au
          // lieu de se faire tronquer.
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-medium">
              {session.username}
            </span>
            {role ? (
              <span className="truncate text-xs text-muted">{role}</span>
            ) : null}
          </div>
        )}
      </div>

      <Button
        aria-label={isCollapsed ? t("sign-out") : undefined}
        // Replié, le bouton ne fait plus toute la largeur : sans `self-center`
        // il resterait collé au bord gauche, décalé de l'avatar au-dessus.
        className={isCollapsed ? "self-center" : "justify-start"}
        fullWidth={!isCollapsed}
        isIconOnly={isCollapsed}
        size="sm"
        variant="ghost"
        onPress={signOut}
      >
        <LogOutIcon className="size-4" />
        {isCollapsed ? null : t("sign-out")}
      </Button>
    </div>
  );
}
